import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  CloseRounded,
  PersonSearchRounded,
} from '@mui/icons-material'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import AddressAutocomplete from '../components/AddressAutocomplete'
import {
  addressFromOption,
  buildJobPayload,
  canSearchPatient,
  defaultJoinRouteId,
  defaultStopKinds,
  describeOpenRun,
  describeOutstanding,
  emptyAddress,
  emptyPatient,
  emptyStop,
  formatAddress,
  hasAddress,
  isVisitOnlyType,
  jobFormFromJourney,
  newJobForm,
  patientSearchCriteria,
  patientSearchKey,
  validateJobForm,
} from './crewJobForm'

// Add / edit a job on a self-dispatched dedicated duty. The hospital tells
// the crew about the job directly, so the crew raise the booking themselves:
// a job type and a pickup are enough to start; the patient and destination
// can be filled in as the crew learn them (both are needed to complete it).

const newRequestId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`

const Card = ({ title, action, children }) => (
  <Paper
    elevation={0}
    sx={{ p: 1.5, borderRadius: 1, border: '1px solid #D8E3E0', bgcolor: '#fff' }}
  >
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ color: '#0F766E', fontWeight: 900, fontSize: '0.9rem' }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Stack>
  </Paper>
)

const formatDob = (dob) => (dob ? moment(dob).format('DD/MM/YYYY') : 'DOB unknown')

const PatientSummary = ({ patient, action }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: 1,
      border: '1px solid #99F6E4',
      bgcolor: '#F0FDFA',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box minWidth={0} flex={1}>
        <Typography sx={{ fontWeight: 900, color: '#0F172A' }}>
          {[patient.firstName, patient.lastName].filter(Boolean).join(' ')}
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '0.84rem' }}>
          {[
            formatDob(patient.dob),
            patient.nhsNumber && `NHS ${patient.nhsNumber}`,
            patient.hospitalNumber && `Hosp. no. ${patient.hospitalNumber}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        <Typography sx={{ color: '#64748B', fontSize: '0.82rem' }}>
          {formatAddress(patient.address) || 'No home address on record'}
        </Typography>
      </Box>
      {action}
    </Stack>
  </Box>
)

// Address search that fills the lines below it; the lines stay editable for
// an address the search can't find (the server then geocodes what was typed).
const AddressFields = ({ label, address, onChange }) => (
  <Stack spacing={1.25}>
    <AddressAutocomplete
      label={label}
      address={address}
      onSelect={(option) => onChange(addressFromOption(option))}
      helperText="Search by address or postcode, or type it in below."
    />
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
      <TextField
        fullWidth
        size="small"
        label="Address line 1"
        value={address.addressLine1}
        onChange={(e) =>
          onChange({
            ...address,
            addressLine1: e.target.value,
            coordinates: null,
            addressMeta: null,
          })
        }
      />
      <TextField
        fullWidth
        size="small"
        label="Town"
        value={address.town}
        onChange={(e) =>
          onChange({
            ...address,
            town: e.target.value,
            coordinates: null,
            addressMeta: null,
          })
        }
      />
      <TextField
        size="small"
        label="Postcode"
        sx={{ minWidth: 130 }}
        value={address.postCode}
        onChange={(e) =>
          onChange({
            ...address,
            postCode: e.target.value,
            coordinates: null,
            addressMeta: null,
          })
        }
      />
    </Stack>
  </Stack>
)

const StopFields = ({
  side,
  stop,
  onChange,
  options,
  wardOptionsFor,
  patientHomeHint,
}) => {
  const isPickup = side === 'pickup'
  const ownName = options?.organisation?.orgName || 'This hospital'
  const kinds = [
    ...(isPickup ? [] : [['pending', 'Not known yet']]),
    ['hospital', ownName],
    ['otherHospital', 'Another hospital'],
    ['patientHome', "Patient's home"],
    ['other', 'Other address'],
  ]
  const hospitalID =
    stop.kind === 'hospital'
      ? options?.organisation?._id
      : stop.organisation?._id
  const wards = hospitalID ? wardOptionsFor(hospitalID) : []

  return (
    <Stack spacing={1.25}>
      <TextField
        select
        fullWidth
        label={isPickup ? 'Collect from' : 'Take to'}
        value={stop.kind}
        onChange={(e) =>
          onChange({ ...emptyStop(e.target.value), note: stop.note })
        }
      >
        {kinds.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>

      {stop.kind === 'pending' && (
        <Alert severity="info">
          You can start the job now. You&apos;ll be asked for the destination
          before you set off with the patient.
        </Alert>
      )}

      {stop.kind === 'otherHospital' && (
        <Autocomplete
          options={options?.organisations || []}
          value={stop.organisation}
          onChange={(event, organisation) =>
            onChange({ ...stop, organisation, ward: '' })
          }
          getOptionLabel={(option) => option?.orgName || ''}
          isOptionEqualToValue={(option, value) =>
            String(option?._id) === String(value?._id)
          }
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {option.orgName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.orgAddressTown, option.orgAddressPostCode]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Hospital" />}
        />
      )}

      {['hospital', 'otherHospital'].includes(stop.kind) && wards.length > 0 && (
        <TextField
          select
          fullWidth
          label="Ward / department"
          value={stop.ward}
          onChange={(e) => onChange({ ...stop, ward: e.target.value })}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">
            {stop.kind === 'hospital' ? 'Requesting ward' : 'Not specified'}
          </MenuItem>
          {wards.map((ward) => (
            <MenuItem key={ward._id} value={ward._id}>
              {[ward.name, ward.internalLocation].filter(Boolean).join(' · ')}
            </MenuItem>
          ))}
        </TextField>
      )}

      {stop.kind === 'patientHome' && (
        <Alert severity={patientHomeHint ? 'warning' : 'info'}>
          {patientHomeHint ||
            "Uses the home address on the patient's record."}
        </Alert>
      )}

      {stop.kind === 'other' && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Place name (optional)"
            placeholder="e.g. Rose Court Care Home"
            value={stop.name}
            onChange={(e) => onChange({ ...stop, name: e.target.value })}
          />
          <AddressFields
            label="Find address"
            address={stop.address}
            onChange={(address) => onChange({ ...stop, address })}
          />
        </>
      )}

      {stop.kind !== 'pending' && (
        <TextField
          fullWidth
          size="small"
          label={isPickup ? 'Pickup note (optional)' : 'Destination note (optional)'}
          placeholder="e.g. bay 3, key safe code, ask for the nurse in charge"
          value={stop.note}
          onChange={(e) => onChange({ ...stop, note: e.target.value })}
        />
      )}
    </Stack>
  )
}

const CrewJobDialog = ({
  open,
  mode = 'create',
  dutyID,
  journey = null,
  // Why the dialog was opened from a blocked workflow step, if it was
  notice = '',
  onClose,
  onSaved,
}) => {
  const isPhone = useIsMobile('sm')
  const isEdit = mode === 'edit'
  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState('')
  const [form, setForm] = useState(newJobForm)
  const [dirty, setDirty] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState({ state: 'idle', key: '', results: [] })
  const [duplicate, setDuplicate] = useState(null)
  const [otherWards, setOtherWards] = useState({})
  const requestIdRef = useRef(null)
  // True once a create has been sent without a clear answer — the same
  // request id is then reused so a retry can't create the job twice
  const attemptedRef = useRef(false)
  const stopsTouchedRef = useRef(false)
  const errorRef = useRef(null)
  const journeyID = journey?._id
  const currentPatient = isEdit
    ? journey?.booking?.patient || journey?.patient
    : null
  // A placeholder still has no patient details; a record the crew typed in
  // for this job is theirs to correct in place
  const canDeferPatient = !isEdit || !!currentPatient?.placeholder
  const editingOwnRecord =
    isEdit && !!journey?.crewJob?.patientOwned && !currentPatient?.placeholder

  // (Re)build the form each time the dialog opens. Deliberately keyed on the
  // journey id, not the journey object — the dashboard poll replaces that
  // object every few seconds and must not wipe what the crew are typing.
  useEffect(() => {
    if (!open) return
    setForm(isEdit && journey ? jobFormFromJourney(journey) : newJobForm())
    setDirty({})
    setError('')
    setSaving(false)
    setSearch({ state: 'idle', key: '', results: [] })
    setDuplicate(null)
    requestIdRef.current = newRequestId()
    attemptedRef.current = false
    stopsTouchedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, journeyID])

  // The form is long on a phone — bring a problem into view when one is raised
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [error])

  useEffect(() => {
    if (!open || !dutyID) return undefined
    let cancelled = false
    setOptionsError('')
    API.get(`/pts/crew/duties/${dutyID}/job-options`)
      .then((res) => {
        if (cancelled) return
        // API never rejects on 4xx/5xx — check the status
        if (res.status === 200) {
          setOptions(res.data)
          // Untouched choice → the sensible default for the runs open now
          setForm((current) =>
            current.joinRouteId === null
              ? { ...current, joinRouteId: defaultJoinRouteId(res.data?.openRuns) }
              : current,
          )
        } else {
          setOptionsError(
            res.data?.error || 'Unable to load the options for this duty.',
          )
        }
      })
      .catch(() => {
        if (!cancelled) setOptionsError('Unable to load the options for this duty.')
      })
    return () => {
      cancelled = true
    }
  }, [open, dutyID])

  // Wards of "another hospital", loaded the first time it is picked
  const otherHospitalIDs = [form.pickup, form.dropoff]
    .filter((stop) => stop.kind === 'otherHospital' && stop.organisation?._id)
    .map((stop) => String(stop.organisation._id))
    .join(',')
  useEffect(() => {
    otherHospitalIDs
      .split(',')
      .filter((id) => id && !otherWards[id])
      .forEach((id) => {
        API.get(`/pts/warddepartmentoptions/${id}`)
          .then((res) => {
            setOtherWards((current) => ({
              ...current,
              [id]: res.status === 200 ? res.data || [] : [],
            }))
          })
          .catch(() => {})
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherHospitalIDs])

  const wardOptionsFor = (hospitalID) =>
    String(hospitalID) === String(options?.organisation?._id)
      ? options?.wards || []
      : otherWards[String(hospitalID)] || []

  const markDirty = (key) => {
    // A changed form is a different request: never let it be swallowed as a
    // retry of the one already sent
    if (attemptedRef.current) {
      attemptedRef.current = false
      requestIdRef.current = newRequestId()
    }
    setDirty((current) => (current[key] ? current : { ...current, [key]: true }))
  }

  const setField = (key, value, dirtyKey = key) => {
    setForm((current) => ({ ...current, [key]: value }))
    markDirty(dirtyKey)
  }

  const setStop = (side, stop) => {
    stopsTouchedRef.current = true
    setField(side, stop)
  }

  const setPatient = (patch) => {
    setForm((current) => ({
      ...current,
      patient: { ...current.patient, ...patch },
    }))
    markDirty('patient')
    setDuplicate(null)
  }

  const setJourneyType = (journeyType) => {
    setForm((current) => {
      // Until the crew choose stops themselves, follow the usual shape of
      // the job type (a discharge leaves the hospital, an admission arrives)
      if (stopsTouchedRef.current) return { ...current, journeyType }
      const kinds = defaultStopKinds(journeyType)
      return {
        ...current,
        journeyType,
        pickup: emptyStop(kinds.pickup),
        dropoff: emptyStop(kinds.dropoff),
      }
    })
    markDirty('journeyType')
  }

  const setPatientMode = (patientMode) => {
    if (!patientMode) return
    setForm((current) => ({
      ...current,
      patientMode,
      existingPatient: patientMode === 'existing' ? current.existingPatient : null,
    }))
    markDirty('patient')
    setDuplicate(null)
  }

  const selectPatient = (summary) => {
    setForm((current) => ({
      ...current,
      patientMode: 'existing',
      existingPatient: summary,
      patient: {
        ...emptyPatient(),
        // What the record already holds — the crew can correct the mobility
        mobilityCategory: summary.mobility?.category || '',
        isBariatric: !!summary.mobility?.isBariatric,
      },
    }))
    markDirty('patient')
    setDuplicate(null)
    setSearch({ state: 'idle', key: '', results: [] })
  }

  const runSearch = async () => {
    const key = patientSearchKey(form.patient)
    setSearch({ state: 'loading', key, results: [] })
    try {
      const res = await API.post(
        `/pts/crew/duties/${dutyID}/patient-lookup`,
        patientSearchCriteria(form.patient),
      )
      if (res.status === 200) {
        // The record being edited is not a "match" for itself
        const results = (res.data?.patients || []).filter(
          (match) => String(match._id) !== String(currentPatient?._id || ''),
        )
        setSearch({ state: 'done', key, results })
        return results
      }
    } catch (e) {
      console.error(e)
    }
    setSearch({ state: 'error', key, results: [] })
    return null
  }

  const submit = async () => {
    if (saving) return
    setError('')

    const problem = validateJobForm({ form, options })
    if (problem) {
      setError(problem)
      return
    }

    setSaving(true)
    try {
      // Before creating a new record, make sure the patient isn't already
      // on the system under the identifiers the crew typed
      if (
        form.patientMode === 'new' &&
        (!isEdit || dirty.patient) &&
        !editingOwnRecord &&
        canSearchPatient(form.patient) &&
        search.key !== patientSearchKey(form.patient)
      ) {
        const results = await runSearch()
        if (results?.length) {
          setError(
            'This patient may already be on the system — choose them below, or confirm they are a new patient and save again.',
          )
          return
        }
      }

      const payload = buildJobPayload({
        form,
        mode,
        dirty,
        clientRequestId: requestIdRef.current,
      })

      if (isEdit && !Object.keys(payload).length) {
        onClose()
        return
      }

      // Outcome unknown until the response lands
      if (!isEdit) attemptedRef.current = true

      const res = isEdit
        ? await API.put(`/pts/crew/journeys/${journeyID}/job`, payload)
        : await API.post(`/pts/crew/duties/${dutyID}/jobs`, payload)

      // The server answered, so nothing is left in doubt
      attemptedRef.current = false

      if (res.status === 200) {
        onSaved?.(res.data)
        return
      }

      if (res.data?.errorCode === 'patientDuplicate' && res.data?.patient) {
        setDuplicate(res.data.patient)
      }
      setError(
        res.data?.errorCode?.startsWith?.('noMatchingGeocode')
          ? "We couldn't find that address on the map. Check it, or pick it from the address search."
          : res.data?.error || 'Unable to save the job. Please try again.',
      )
    } catch (e) {
      console.error(e)
      setError(
        'Unable to save the job — check your signal and try again. Nothing has been lost.',
      )
    } finally {
      setSaving(false)
    }
  }

  const visitOnly = isVisitOnlyType(form.journeyType)
  const outstanding = journey?.crewJob?.outstanding || []
  const needsHomeAddress =
    [form.pickup, ...(visitOnly ? [] : [form.dropoff])].some(
      (stop) => stop.kind === 'patientHome',
    )
  const patientHomeHint = (() => {
    if (form.patientMode === 'later') {
      return "Add the patient's details and home address below to use their home."
    }
    if (form.patientMode === 'new' && !hasAddress(form.patient.address)) {
      return "Add the patient's home address below."
    }
    if (
      form.patientMode === 'existing' &&
      form.existingPatient &&
      !form.existingPatient.hasAddress &&
      !hasAddress(form.patient.address)
    ) {
      return 'This patient has no home address on record — add it below.'
    }
    return ''
  })()

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: '#EEF4F3' } }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: '#0F172A', color: '#fff' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontSize: '0.78rem', color: '#99F6E4' }}>
              {options?.organisation?.orgName || 'Dedicated duty'}
            </Typography>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 950, color: '#fff' }}>
              {isEdit ? 'Job details' : 'Add a job'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }}
          >
            <CloseRounded />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          {notice && <Alert severity="warning">{notice}</Alert>}
          {isEdit && !notice && outstanding.length > 0 && (
            <Alert severity="warning">
              Still needed before this job can be completed:{' '}
              {describeOutstanding(outstanding)}.
            </Alert>
          )}
          {optionsError && <Alert severity="error">{optionsError}</Alert>}
          {!options && !optionsError && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}

          {options && (
            <>
              <Card title="Job">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    select
                    fullWidth
                    label="Type of job"
                    value={form.journeyType}
                    disabled={isEdit}
                    onChange={(e) => setJourneyType(e.target.value)}
                  >
                    {(options.journeyTypes || []).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label="Priority"
                    value={form.priority}
                    onChange={(e) => setField('priority', e.target.value)}
                  >
                    {(options.priorities || []).map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                {(options.wards || []).length > 0 && (
                  <TextField
                    select
                    fullWidth
                    required
                    label="Requesting ward / department"
                    value={form.wardDepartment}
                    onChange={(e) =>
                      setField('wardDepartment', e.target.value, 'ward')
                    }
                  >
                    {options.wards.map((ward) => (
                      <MenuItem key={ward._id} value={ward._id}>
                        {[ward.name, ward.internalLocation]
                          .filter(Boolean)
                          .join(' · ')}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Card>

              {!isEdit && (options.openRuns || []).length > 0 && (
                <Card title="Travelling with">
                  <Typography variant="body2" color="text.secondary">
                    Taking this patient at the same time as a job you already
                    have? Add it to that run so control see them travelling
                    together.
                  </Typography>
                  <RadioGroup
                    // Nothing ticked until the crew choose
                    value={form.joinRouteId === null ? 'unchosen' : form.joinRouteId}
                    onChange={(e) => setField('joinRouteId', e.target.value, 'run')}
                  >
                    {options.openRuns.map((run) => (
                      <FormControlLabel
                        key={run.routeId}
                        value={String(run.routeId)}
                        control={<Radio />}
                        label={
                          <Box py={0.5}>
                            <Typography sx={{ fontWeight: 700 }}>
                              With {run.label.toLowerCase()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {describeOpenRun(run)}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                    <FormControlLabel
                      value=""
                      control={<Radio />}
                      label={
                        <Typography sx={{ fontWeight: 700 }}>
                          Separate run
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </Card>
              )}

              <Card title={visitOnly ? 'Visit location' : 'Pickup'}>
                <StopFields
                  side="pickup"
                  stop={form.pickup}
                  onChange={(stop) => setStop('pickup', stop)}
                  options={options}
                  wardOptionsFor={wardOptionsFor}
                  patientHomeHint={patientHomeHint}
                />
              </Card>

              {!visitOnly && (
                <Card title="Destination">
                  <StopFields
                    side="dropoff"
                    stop={form.dropoff}
                    onChange={(stop) => setStop('dropoff', stop)}
                    options={options}
                    wardOptionsFor={wardOptionsFor}
                    patientHomeHint={patientHomeHint}
                  />
                </Card>
              )}

              <Card title="Patient">
                {canDeferPatient && (
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    value={
                      form.patientMode === 'existing' ? 'new' : form.patientMode
                    }
                    onChange={(event, value) => setPatientMode(value)}
                  >
                    <ToggleButton value="later" sx={{ fontWeight: 800 }}>
                      Add later
                    </ToggleButton>
                    <ToggleButton value="new" sx={{ fontWeight: 800 }}>
                      Add patient now
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}

                {form.patientMode === 'later' && (
                  <Typography sx={{ color: '#64748B', fontSize: '0.86rem' }}>
                    No patient details yet. You can start the job and add them
                    when the hospital gives them to you — they are needed
                    before the job can be completed.
                  </Typography>
                )}

                {form.patientMode === 'existing' && form.existingPatient && (
                  <>
                    <PatientSummary
                      patient={form.existingPatient}
                      action={
                        <Button
                          size="small"
                          onClick={() => setPatientMode('new')}
                          sx={{ fontWeight: 800, textTransform: 'none' }}
                        >
                          Change
                        </Button>
                      }
                    />
                    {needsHomeAddress && !form.existingPatient.hasAddress && (
                      <AddressFields
                        label="Find the patient's home address"
                        address={form.patient.address}
                        onChange={(address) => setPatient({ address })}
                      />
                    )}
                  </>
                )}

                {form.patientMode === 'new' && (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        fullWidth
                        label="Hospital number"
                        value={form.patient.hospitalNumber}
                        onChange={(e) =>
                          setPatient({ hospitalNumber: e.target.value })
                        }
                      />
                      <TextField
                        fullWidth
                        label="NHS number"
                        inputProps={{ inputMode: 'numeric' }}
                        value={form.patient.nhsNumber}
                        onChange={(e) => setPatient({ nhsNumber: e.target.value })}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        fullWidth
                        required
                        label="First name"
                        value={form.patient.firstName}
                        onChange={(e) => setPatient({ firstName: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        required
                        label="Surname"
                        value={form.patient.lastName}
                        onChange={(e) => setPatient({ lastName: e.target.value })}
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      type="date"
                      label="Date of birth"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: moment().format('YYYY-MM-DD') }}
                      value={form.patient.dob}
                      onChange={(e) => setPatient({ dob: e.target.value })}
                    />

                    <Button
                      variant="outlined"
                      startIcon={
                        search.state === 'loading' ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PersonSearchRounded />
                        )
                      }
                      disabled={
                        !canSearchPatient(form.patient) ||
                        search.state === 'loading'
                      }
                      onClick={runSearch}
                      sx={{ fontWeight: 800, textTransform: 'none' }}
                    >
                      Check for an existing patient
                    </Button>
                    {!canSearchPatient(form.patient) && (
                      <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>
                        Enter a hospital number, an NHS number, or a surname
                        with a date of birth to check whether the patient is
                        already on the system.
                      </Typography>
                    )}
                    {search.state === 'error' && (
                      <Alert severity="warning">
                        Unable to check for existing patients right now.
                      </Alert>
                    )}
                    {search.state === 'done' && search.results.length === 0 && (
                      <Alert severity="success">
                        {editingOwnRecord
                          ? 'No other patient on the system matches these details.'
                          : 'No existing patient matches — they will be added as a new patient.'}
                      </Alert>
                    )}
                    {search.state === 'done' &&
                      search.results.map((match) => (
                        <PatientSummary
                          key={match._id}
                          patient={match}
                          action={
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => selectPatient(match)}
                              sx={{ fontWeight: 800, textTransform: 'none' }}
                            >
                              Use
                            </Button>
                          }
                        />
                      ))}
                    {duplicate && (
                      <PatientSummary
                        patient={duplicate}
                        action={
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => selectPatient(duplicate)}
                            sx={{ fontWeight: 800, textTransform: 'none' }}
                          >
                            Use
                          </Button>
                        }
                      />
                    )}

                    <AddressFields
                      label={
                        needsHomeAddress
                          ? 'Find the home address'
                          : 'Find the home address (optional)'
                      }
                      address={form.patient.address}
                      onChange={(address) => setPatient({ address })}
                    />
                    {hasAddress(form.patient.address) && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setPatient({ address: emptyAddress() })}
                        sx={{
                          alignSelf: 'flex-start',
                          fontWeight: 700,
                          textTransform: 'none',
                        }}
                      >
                        Clear home address
                      </Button>
                    )}
                    <TextField
                      fullWidth
                      label="Contact number (optional)"
                      inputProps={{ inputMode: 'tel' }}
                      value={form.patient.phoneNumber1}
                      onChange={(e) => setPatient({ phoneNumber1: e.target.value })}
                    />
                  </>
                )}

                {form.patientMode !== 'later' && (
                  <>
                    <TextField
                      select
                      fullWidth
                      label="Mobility"
                      value={form.patient.mobilityCategory}
                      onChange={(e) =>
                        setPatient({ mobilityCategory: e.target.value })
                      }
                    >
                      {(options.mobilityCategories || []).map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.patient.isBariatric}
                          onChange={(e) =>
                            setPatient({ isBariatric: e.target.checked })
                          }
                        />
                      }
                      label="Bariatric"
                    />
                  </>
                )}

                {form.patientMode === 'new' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Patient notes (optional)"
                    placeholder="Anything the next crew should know about this patient"
                    value={form.patient.clinicalNotes}
                    onChange={(e) => setPatient({ clinicalNotes: e.target.value })}
                  />
                )}
              </Card>

              <Card title="Notes">
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Notes for this job (optional)"
                  placeholder="e.g. who asked for the job, handover details"
                  value={form.bookingNotes}
                  onChange={(e) => setField('bookingNotes', e.target.value, 'notes')}
                />
              </Card>
            </>
          )}

          {error && (
            <Alert severity="error" ref={errorRef}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <Box
        sx={{
          p: 1.5,
          bgcolor: 'rgba(238,244,243,0.94)',
          borderTop: '1px solid #D8E3E0',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          disabled={!options || saving}
          startIcon={
            saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />
          }
          onClick={submit}
          sx={{ borderRadius: 1, py: 1.35, fontWeight: 950, textTransform: 'none' }}
        >
          {saving ? 'Saving...' : isEdit ? 'Save details' : 'Add job'}
        </Button>
      </Box>
    </Dialog>
  )
}

export default CrewJobDialog
